<div class="identity-verify">
  <header class="identity-verify__header">
    <div class="identity-verify__icon identity-verify__icon--intro" aria-hidden="true">
      <img src="/public/svgs/identity-id.svg" alt="" width="28" height="28" />
    </div>
    <h1 class="identity-verify__title">
      {{ 'app.identity.verify.pageTitle' | translate }}
    </h1>
    <p class="identity-verify__subtitle">
      {{ 'app.identity.verify.pageSubtitle' | translate }}
    </p>
  </header>

  @switch (step()) {
    @case ('intro') {
      <section class="identity-verify__card">
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.intro.title' | translate }}
        </h2>
        <p class="identity-verify__card-copy">
          {{ 'app.identity.verify.intro.body' | translate }}
        </p>
        <ul class="identity-verify__list">
          <li>{{ 'app.identity.verify.intro.point1' | translate }}</li>
          <li>{{ 'app.identity.verify.intro.point2' | translate }}</li>
          <li>{{ 'app.identity.verify.intro.point3' | translate }}</li>
        </ul>
        <div class="identity-verify__actions">
          <app-parent-button
            variant="primary"
            [fullWidth]="true"
            [loading]="isBusy()"
            (clicked)="startVerification()"
          >
            {{ 'app.identity.verify.intro.cta' | translate }}
          </app-parent-button>
          <app-parent-button variant="ghost" [fullWidth]="true" (clicked)="skipForNow()">
            {{ 'app.identity.verify.skip' | translate }}
          </app-parent-button>
        </div>
      </section>
    }

    @case ('consent') {
      <section class="identity-verify__card">
        <app-identity-consent-card
          titleKey="app.identity.verify.consent.title"
          bodyKey="app.identity.verify.consent.body"
          leadLabelKey="app.identity.verify.consent.lead"
          [pointKeys]="[
            'app.identity.verify.consent.point1',
            'app.identity.verify.consent.point2',
            'app.identity.verify.consent.point3'
          ]"
          noteKey="app.identity.verify.consent.note"
          checkboxLabelKey="app.identity.verify.consent.checkbox"
          panelAriaLabel="Verification consent details"
          leadIconSrc="/public/svgs/identity-id.svg"
          [busy]="isBusy()"
          [(checked)]="consentAccepted"
          (back)="goBack()"
          (continue)="goToDocumentType()"
        />
      </section>
    }

    @case ('documentType') {
      <section class="identity-verify__card">
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.documentType.title' | translate }}
        </h2>
        <div class="identity-verify__segment">
          <button
            type="button"
            class="identity-verify__segment-btn"
            [class.identity-verify__segment-btn--active]="documentType() === 'passport'"
            (click)="selectDocumentType('passport')"
          >
            {{ 'app.identity.verify.documentType.passport' | translate }}
          </button>
          <button
            type="button"
            class="identity-verify__segment-btn"
            [class.identity-verify__segment-btn--active]="documentType() === 'id_card'"
            (click)="selectDocumentType('id_card')"
          >
            {{ 'app.identity.verify.documentType.idCard' | translate }}
          </button>
        </div>
      </section>
    }

    @case ('documentFront') {
      <section class="identity-verify__card">
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.documentFront.title' | translate }}
        </h2>
        <div class="identity-verify__capture-frame">
          <div class="identity-verify__capture-placeholder">
            <span>{{ 'app.identity.verify.captureHint' | translate }}</span>
          </div>
        </div>
        <div class="identity-verify__actions">
          <app-parent-button variant="ghost" (clicked)="goBack()">
            {{ 'app.identity.verify.back' | translate }}
          </app-parent-button>
          <app-parent-button variant="primary" (clicked)="nextFromDocumentFront()">
            {{ 'app.identity.verify.capture' | translate }}
          </app-parent-button>
        </div>
      </section>
    }

    @case ('documentBack') {
      <section class="identity-verify__card">
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.documentBack.title' | translate }}
        </h2>
        <div class="identity-verify__capture-frame">
          <div class="identity-verify__capture-placeholder">
            <span>{{ 'app.identity.verify.captureHint' | translate }}</span>
          </div>
        </div>
        <div class="identity-verify__actions">
          <app-parent-button variant="ghost" (clicked)="goBack()">
            {{ 'app.identity.verify.back' | translate }}
          </app-parent-button>
          <app-parent-button variant="primary" (clicked)="nextFromDocumentBack()">
            {{ 'app.identity.verify.capture' | translate }}
          </app-parent-button>
        </div>
      </section>
    }

    @case ('selfie') {
      <section class="identity-verify__card">
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.selfie.title' | translate }}
        </h2>
        <p class="identity-verify__card-copy">
          {{ 'app.identity.verify.selfie.body' | translate }}
        </p>
        <div class="identity-verify__capture-frame identity-verify__capture-frame--round">
          <div class="identity-verify__capture-placeholder">
            <span>{{ 'app.identity.verify.selfie.hint' | translate }}</span>
          </div>
        </div>
        @if (showSimulate()) {
          <label class="identity-verify__consent identity-verify__consent--dev">
            <input
              type="checkbox"
              [checked]="simulateFailed()"
              (change)="simulateFailed.set($any($event.target).checked)"
            />
            <span>{{ 'app.identity.verify.devSimulateFail' | translate }}</span>
          </label>
        }
        <div class="identity-verify__actions">
          <app-parent-button variant="ghost" (clicked)="goBack()">
            {{ 'app.identity.verify.back' | translate }}
          </app-parent-button>
          <app-parent-button
            variant="primary"
            [loading]="isBusy()"
            (clicked)="completeCapture()"
          >
            {{ 'app.identity.verify.submit' | translate }}
          </app-parent-button>
        </div>
      </section>
    }

    @case ('processing') {
      <section class="identity-verify__card identity-verify__card--center">
        <span class="identity-verify__spinner" aria-hidden="true"></span>
        <h2 class="identity-verify__card-title">
          {{ 'app.identity.verify.processing.title' | translate }}
        </h2>
        <p class="identity-verify__card-copy">
          {{ 'app.identity.verify.processing.body' | translate }}
        </p>
      </section>
    }

    @case ('result') {
      <section class="identity-verify__card identity-verify__card--center">
        @if (isVerified()) {
          <div class="identity-verify__icon identity-verify__icon--success" aria-hidden="true">✓</div>
          <h2 class="identity-verify__card-title">
            {{ 'app.identity.verify.result.successTitle' | translate }}
          </h2>
          <p class="identity-verify__card-copy">
            {{ 'app.identity.verify.result.successBody' | translate }}
          </p>
        } @else {
          <div class="identity-verify__icon identity-verify__icon--error" aria-hidden="true">!</div>
          <h2 class="identity-verify__card-title">
            {{ 'app.identity.verify.result.failedTitle' | translate }}
          </h2>
          <p class="identity-verify__card-copy">
            {{ 'app.identity.verify.result.failedBody' | translate }}
          </p>
        }
        <div class="identity-verify__actions">
          @if (isVerified()) {
            <app-parent-button variant="primary" [fullWidth]="true" (clicked)="finish()">
              {{ 'app.identity.verify.result.continue' | translate }}
            </app-parent-button>
          } @else {
            <app-parent-button variant="primary" [fullWidth]="true" (clicked)="step.set('intro')">
              {{ 'app.identity.verify.result.retry' | translate }}
            </app-parent-button>
          }
        </div>
      </section>
    }
  }
</div>
